import React from 'react';
import { POS_GOLD } from '../../../../../config/posRoomStatus';
import { formatWeekdayShort } from '../../../../../utils/formatters';
import { useConfig } from '../../../../../contexts/ConfigContext';
import CalendarFilterBar from './CalendarFilterBar';
import CalendarLegend from './CalendarLegend';
import { ReminderChips, useCalendarReminders } from './CalendarReminders';
import {
  PRIMARY_COLOR, STATUS_CONFIG, BLOCK_STYLE, TOTAL_CALENDAR_HOURS
} from './constants';
import {
  formatLocalDate, getInitials, getTerritoryCode,
  getTimePosition, getTimeHeight, getEventsForDate,
  calculateEventColumns, getAvailabilitySlots
} from './helpers';

interface CalendarFilters {
  search: string;
  status: string;
  territory_id: string;
  assigned_to: string;
  context: 'all' | 'pos' | 'cmb';
  show_blocks: boolean;
  show_reminders: boolean;
}

interface FilterOption {
  value: string;
  label: string;
}

interface CalendarBookingRow {
  id: string;
  booking_date_sast: string;
  start_time: string;
  end_time?: string;
  customer_name?: string;
  territory_name?: string;
  status: string;
  operator_names?: string[];
  booking_reference?: string;
  assigned_to_name?: string;
  is_pos?: boolean;
  [key: string]: unknown;
}

interface OperatorBlockRow {
  id: string;
  operator_id: string | null;
  reason?: string;
  operator_name?: string;
  start_date: string;
  end_date: string;
  start_time?: string | null;
  end_time?: string | null;
  is_all_day?: boolean;
  [key: string]: unknown;
}

interface OperatorPatternRow {
  id: string;
  operator_id: string;
  days: string[];
  start_time: string;
  end_time: string;
  is_active: boolean;
  [key: string]: unknown;
}

interface WeekViewProps {
  selectedDate: Date;
  setSelectedDate: React.Dispatch<React.SetStateAction<Date>>;
  setViewMode: React.Dispatch<React.SetStateAction<string>>;
  isMobile: boolean;
  calendarFilters: CalendarFilters;
  setCalendarFilters: React.Dispatch<React.SetStateAction<CalendarFilters>>;
  calendarFiltersExpanded: boolean;
  setCalendarFiltersExpanded: React.Dispatch<React.SetStateAction<boolean>>;
  activeCalendarFilterCount: number;
  statusOptions: FilterOption[];
  territoryOptions: FilterOption[];
  operatorOptions: FilterOption[];
  filteredCalendarBookings: CalendarBookingRow[];
  showBlocks: boolean;
  operatorBlocks: OperatorBlockRow[];
  operatorPatterns: OperatorPatternRow[];
  handleViewBooking: (booking: CalendarBookingRow) => void;
  handlePrevWeek: () => void;
  handleNextWeek: () => void;
  handlePrevDay: () => void;
  handleNextDay: () => void;
  handlePrevMonth: () => void;
  handleNextMonth: () => void;
  handleToday: () => void;
  currentMonth: Date;
  viewMode: string;
  DAY_NAMES: readonly string[];
}

export default function WeekView({
  selectedDate,
  setSelectedDate,
  setViewMode,
  isMobile,
  calendarFilters,
  setCalendarFilters,
  calendarFiltersExpanded,
  setCalendarFiltersExpanded,
  activeCalendarFilterCount,
  statusOptions,
  territoryOptions,
  operatorOptions,
  filteredCalendarBookings,
  showBlocks,
  operatorBlocks,
  operatorPatterns,
  handleViewBooking,
  handlePrevWeek,
  handleNextWeek,
  handlePrevDay,
  handleNextDay,
  handlePrevMonth,
  handleNextMonth,
  handleToday,
  currentMonth,
  viewMode,
  DAY_NAMES
}: WeekViewProps) {
  const reminders = useCalendarReminders(calendarFilters);
  const startOfWeek = new Date(selectedDate);
  startOfWeek.setDate(selectedDate.getDate() - selectedDate.getDay());

  const weekDays = Array.from({ length: 7 }, (_, i) => {
    const day = new Date(startOfWeek);
    day.setDate(startOfWeek.getDate() + i);
    return day;
  });

  const { config } = useConfig();
  const hours = Array.from({ length: 11 }, (_, i) => i + 8);
  // Country-local "today" so travelling super-admins see the country's clock
  const todayStr = formatLocalDate(new Date(), config?.timezone);
  const hourHeight = isMobile ? 40 : 55;

  return (
    <div className="card">
      <CalendarFilterBar
        calendarFilters={calendarFilters}
        setCalendarFilters={setCalendarFilters}
        calendarFiltersExpanded={calendarFiltersExpanded}
        setCalendarFiltersExpanded={setCalendarFiltersExpanded}
        activeCalendarFilterCount={activeCalendarFilterCount}
        statusOptions={statusOptions}
        territoryOptions={territoryOptions}
        operatorOptions={operatorOptions}
        filteredCalendarBookings={filteredCalendarBookings}
        showBlocks={showBlocks}
        viewMode={viewMode as 'day' | 'week' | 'month' | 'list'}
        isMobile={isMobile}
        handlePrevDay={handlePrevDay}
        handlePrevWeek={handlePrevWeek}
        handlePrevMonth={handlePrevMonth}
        handleNextDay={handleNextDay}
        handleNextWeek={handleNextWeek}
        handleNextMonth={handleNextMonth}
        handleToday={handleToday}
        selectedDate={selectedDate}
        currentMonth={currentMonth}
      />

      <div className="cal-week-scroll">
        <div className="cal-week-grid">
          {/* Time labels */}
          <div className="cal-time-labels" style={{ width: 'var(--cal-week-time-w)' }}>
            <div className="cal-day-header" style={{ height: 'var(--cal-week-header-h)' }} />
            {hours.map(hour => (
              <div
                key={hour}
                className="cal-time-label"
                style={{ height: `${hourHeight}px`, fontSize: isMobile ? '0.6rem' : '0.75rem', padding: isMobile ? '4px 2px' : '4px 8px' }}
              >
                {hour}:00
              </div>
            ))}
          </div>

          {/* Day columns */}
          {weekDays.map((day, dayIndex) => {
            const dateStr = formatLocalDate(day, config?.timezone);
            const isDayToday = dateStr === todayStr;
            const dayEvents = getEventsForDate(dateStr, filteredCalendarBookings, showBlocks, operatorBlocks, calendarFilters);
            const eventsWithColumns = calculateEventColumns(dayEvents);

            return (
              <div key={dayIndex} className="cal-week-day-col">
                {/* Day header */}
                <div
                  className={`cal-day-header ${isDayToday ? 'cal-day-header--today' : ''}`}
                  onClick={isMobile ? () => { setSelectedDate(day); setViewMode('day'); } : undefined}
                  style={isDayToday ? { background: `${PRIMARY_COLOR}08` } : undefined}
                >
                  <div className="cal-day-header-weekday">
                    {formatWeekdayShort(day)}
                  </div>
                  <div className="cal-day-header-date">{day.getDate()}</div>
                  {dayEvents.length > 0 && (
                    <div className="cal-day-header-count">
                      {dayEvents.filter(e => e.type === 'booking').length} bookings
                    </div>
                  )}
                  <ReminderChips reminders={reminders.forDate(dateStr)} compact={isMobile} limit={1} />
                </div>

                {/* Day events container */}
                <div
                  className="cal-events-col"
                  style={{
                    height: `${hours.length * hourHeight}px`,
                    background: isDayToday ? `${PRIMARY_COLOR}04` : '#fff'
                  }}
                >
                  {/* Availability background */}
                  {calendarFilters.assigned_to !== 'all' && getAvailabilitySlots(calendarFilters.assigned_to, day, operatorPatterns, DAY_NAMES).map((slot, slotIdx) => {
                    const slotTop = getTimePosition(`${Math.floor(slot.startHour)}:${Math.round((slot.startHour % 1) * 60).toString().padStart(2, '0')}`);
                    const slotHeight = ((slot.endHour - slot.startHour) / TOTAL_CALENDAR_HOURS) * 100;
                    return (
                      <div
                        key={`avail-${slotIdx}`}
                        className="cal-avail-slot"
                        style={{ top: `${slotTop}%`, height: `${slotHeight}%` }}
                        title="Available hours"
                      />
                    );
                  })}

                  {/* Hour grid lines */}
                  {hours.map((hour, i) => (
                    <div
                      key={hour}
                      className="cal-hour-line"
                      style={{ top: `${i * hourHeight}px`, height: `${hourHeight}px` }}
                    />
                  ))}

                  {/* Events */}
                  {eventsWithColumns.map(event => {
                    const top = getTimePosition(event.startTime);
                    const height = getTimeHeight(event.startTime, event.endTime);
                    const width = 100 / (event.totalColumns || 1);
                    const left = (event.column || 0) * width;

                    if (event.type === 'block') {
                      const block = event.data as OperatorBlockRow;
                      return (
                        <div
                          key={`block-${event.id}`}
                          className="cal-event-block"
                          style={{
                            top: `${top}%`,
                            height: `${height}%`,
                            left: `calc(${left}% + 2px)`,
                            width: `calc(${width}% - 4px)`,
                            background: BLOCK_STYLE.bg,
                            borderLeft: `3px solid ${BLOCK_STYLE.border}`,
                            fontSize: isMobile ? '0.55rem' : '0.7rem',
                            padding: isMobile ? '2px' : '4px'
                          }}
                          title={`Blocked: ${block.operator_name} - ${block.reason || 'No reason'}`}
                        >
                          <span
                            className="cal-badge-initials"
                            style={{
                              background: BLOCK_STYLE.border,
                              color: 'var(--color-white)',
                              width: '16px',
                              height: '16px',
                              fontSize: '0.5rem',
                              marginBottom: '2px'
                            }}
                          >
                            {getInitials(block.operator_name)}
                          </span>
                          <div style={{ fontSize: 'var(--text-2xs)', color: BLOCK_STYLE.color, fontWeight: 500 }}>
                            {block.reason || 'Blocked'}
                          </div>
                        </div>
                      );
                    }

                    const booking = event.data as CalendarBookingRow;
                    const status = (STATUS_CONFIG[booking.status] || STATUS_CONFIG.pending)!;
                    const territoryCode = getTerritoryCode(booking.territory_name);
                    const operatorInitials = Array.isArray(booking.operator_names) && booking.operator_names.length > 0
                      ? getInitials(booking.operator_names[0])
                      : null;

                    const brandColour = booking.is_pos ? POS_GOLD : 'var(--color-primary)';
                    return (
                      <div
                        key={`booking-${event.id}`}
                        className="cal-event"
                        onClick={() => handleViewBooking(booking)}
                        style={{
                          top: `${top}%`,
                          height: `${height}%`,
                          left: `calc(${left}% + 2px)`,
                          width: `calc(${width}% - 4px)`,
                          background: status.bg,
                          borderLeft: `3px solid ${status.color}`,
                          fontSize: isMobile ? '0.55rem' : '0.65rem',
                          padding: 0,
                          overflow: 'hidden',
                          display: 'flex',
                          flexDirection: 'column',
                        }}
                        title={`${booking.booking_reference} - ${booking.customer_name}${booking.is_pos ? ' [POS]' : ''} (${booking.territory_name || 'Unknown'})`}
                      >
                        {/* Brand banner — short customer name on a violet/gold strip */}
                        <div style={{
                          background: brandColour,
                          color: 'var(--color-white)',
                          padding: isMobile ? '1px 3px' : '1px 4px',
                          fontSize: isMobile ? '0.5rem' : '0.6rem',
                          fontWeight: 700,
                          lineHeight: 1.2,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}>
                          {booking.customer_name?.split(' ')[0] || 'No name'}{booking.is_pos ? ' · POS' : ''}
                        </div>
                        {/* Body */}
                        <div style={{ padding: isMobile ? '2px 3px' : '3px 4px', display: 'flex', flexDirection: 'column', gap: '1px' }}>
                          <div className="cal-event-time" style={{ lineHeight: 1.1 }}>
                            {booking.start_time}
                          </div>
                          <div style={{ display: 'flex', gap: '3px' }}>
                            {territoryCode && (
                              <span className="cal-badge-territory" style={{ padding: '0 3px', fontSize: '0.5rem', lineHeight: '12px' }}>
                                {territoryCode}
                              </span>
                            )}
                            {operatorInitials && (
                              <span
                                className="cal-badge-initials"
                                style={{ width: 'var(--cal-week-badge-size)', height: 'var(--cal-week-badge-size)', fontSize: 'var(--cal-week-badge-font)' }}
                              >
                                {operatorInitials}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <CalendarLegend isMobile={isMobile} showBlocks={showBlocks} calendarFilters={calendarFilters} showReminders={reminders.on} />

      {isMobile && (
        <div className="cal-mobile-hint">Tap a day header to view details</div>
      )}
    </div>
  );
}
